import { GameServices } from '../../game-services';
import { DeckPileViewModel } from './deck-pile-view-model';
import { WastePileViewModel } from './waste-pile-view-model';
import { FoundationPileViewModel } from './foundation-pile-view-model';
import { TableauPileViewModel } from './tableau-pile-view-model';
import { PileType, PileId } from 'src/games/phasersolitaire/logic/logic-types';
import { CardViewModel } from '../card/card-view-model';
import { Subject } from 'rxjs';
import { DebugControlsViewModel } from './debug-controls-view-model';
import { delay } from 'src/games/common/helper';
import { PossibleMove } from 'src/games/phasersolitaire/logic/possible-moves-finder';
import { Card } from 'src/games/phasersolitaire/logic/card/card';
import { StartViewModel } from './start-view-model';
import { GameState } from 'game-logic';

export type DragData = {
  dragX: number;
  dragY: number;
  cardViewModel: CardViewModel;
};

export class BoardViewModel {
  public get onNewDealStartedTrigger() {
    return this.gameServices.gameRunnerService.onNewDealStartedTrigger;
  }
  public get onPossibleHintsTrigger() {
    return this.gameServices.gameRunnerService.onPossibleHintsTrigger;
  }
  public get onClearBoard$() {
    return this.onClearBoard;
  }
  public get onBoardChangedTrigger() {
    return this.gameServices.gameRunnerService.onBoardChangedTrigger;
  }
  public get onGameStateChangedTrigger() {
    return this.gameServices.gameRunnerService.onGameStateChangedTrigger;
  }
  public get onCardDragStartTrigger() {
    return this.cardDragStartTrigger.asObservable();
  }
  public get onCardDragTrigger() {
    return this.cardDragTrigger.asObservable();
  }
  public get onCardDragEndTrigger() {
    return this.cardDragEndTrigger.asObservable();
  }
  public get allCardViewModels() {
    return this.cardViewModels;
  }

  public readonly tableausPileViewModels: TableauPileViewModel[] = [];
  public readonly foundationPileViewModels: FoundationPileViewModel[] = [];
  public readonly wastePileViewModel: WastePileViewModel;
  public readonly deckPileViewModel: DeckPileViewModel;
  public readonly startViewModel: StartViewModel;
  public readonly debugControlsViewModel: DebugControlsViewModel;

  private readonly gameServices: GameServices;
  private readonly onClearBoard: Subject<void>;
  private readonly cardDragStartTrigger: Subject<DragData>;
  private readonly cardDragTrigger: Subject<DragData>;
  private readonly cardDragEndTrigger: Subject<DragData>;
  private autoFinishingGame: boolean;
  private instantFinishScaler = 1.0;

  private cardViewModels: CardViewModel[] = [];

  constructor(gameServices: GameServices) {
    this.gameServices = gameServices;
    this.onClearBoard = new Subject<void>();
    this.cardDragStartTrigger = new Subject<DragData>();
    this.cardDragTrigger = new Subject<DragData>();
    this.cardDragEndTrigger = new Subject<DragData>();
    this.autoFinishingGame = false;

    const tableauPiles = [
      PileId.Tableau_01,
      PileId.Tableau_02,
      PileId.Tableau_03,
      PileId.Tableau_04,
      PileId.Tableau_05,
      PileId.Tableau_06,
      PileId.Tableau_07,
    ];

    tableauPiles.forEach((pileId, index) => {
      this.tableausPileViewModels.push(new TableauPileViewModel(gameServices, pileId, index));
    });

    const foundationPiles = [
      PileId.Foundation_01,
      PileId.Foundation_02,
      PileId.Foundation_03,
      PileId.Foundation_04,
    ];

    foundationPiles.forEach((pileId, index) => {
      this.foundationPileViewModels.push(new FoundationPileViewModel(gameServices, pileId, index));
    });

    this.wastePileViewModel = new WastePileViewModel(gameServices);
    this.deckPileViewModel = new DeckPileViewModel(gameServices);
    this.startViewModel = new StartViewModel(gameServices);
    this.debugControlsViewModel = new DebugControlsViewModel(gameServices);
  }

  public showStartButton(showButton: boolean) {
    this.startViewModel.showStartButton(showButton);
  }

  public setInstantFinish() {
    this.instantFinishScaler = 0.05;
  }

  public resetGameScene() {
    this.onClearBoard.next();
    this.cardViewModels.length = 0;
  }

  public spawnDeck(): boolean {
    const game = this.gameServices.gameRunnerService.currentGame;
    if (game === undefined) {
      return false;
    }

    this.autoFinishingGame = false;

    this.cardViewModels = game.board.deck.allCards.map((card, index) => {
      const cardViewModel = new CardViewModel(
        this.gameServices,
        game.gameLogicDepth,
        card,
        index,
        PileType.Deck,
        PileId.Deck,
      );
      cardViewModel.onDragStartAction = (dragData: DragData) => {
        this.cardDragStartTrigger.next(dragData);
      };
      cardViewModel.onDragAction = (dragData: DragData) => {
        this.cardDragTrigger.next(dragData);
      };
      cardViewModel.onDragEndAction = (dragData: DragData) => {
        this.cardDragEndTrigger.next(dragData);
      };
      cardViewModel.onShakeFromNoMatchesAction = (cardViewModel: CardViewModel) => {
        this.shakeCardStack(cardViewModel);
      };
      return cardViewModel;
    });

    return true;
  }

  public boardChanged() {
    const game = this.gameServices.gameRunnerService.currentGame;
    if (game === undefined) {
      return;
    }

    game.board.foundations.forEach((pile) => {
      const allCards = pile.allCards;
      for (let i = 0; i < allCards.length; ++i) {
        const cardViewModel = this.cardViewModels.find((vm) =>
          vm.matchesCardIndex(allCards[i].index),
        );
        cardViewModel?.refreshPosition(i, allCards[i], PileType.Foundation, pile.id);
      }
    });

    game.board.tableaus.forEach((pile) => {
      const allCards = pile.allCards;
      for (let i = 0; i < allCards.length; ++i) {
        const cardViewModel = this.cardViewModels.find((vm) =>
          vm.matchesCardIndex(allCards[i].index),
        );
        cardViewModel?.refreshPosition(i, allCards[i], PileType.Tableau, pile.id);
      }
    });

    const allDeckCards = game.board.deck.allCards;
    for (let i = 0; i < allDeckCards.length; ++i) {
      const cardViewModel = this.cardViewModels.find((vm) =>
        vm.matchesCardIndex(allDeckCards[i].index),
      );
      cardViewModel?.refreshPosition(i, allDeckCards[i], PileType.Deck, PileId.Deck);
    }

    const allWasteCards = game.board.waste.allCards;
    for (let i = 0; i < allWasteCards.length; ++i) {
      const cardViewModel = this.cardViewModels.find((vm) =>
        vm.matchesCardIndex(allWasteCards[i].index),
      );
      cardViewModel?.refreshPosition(i, allWasteCards[i], PileType.Waste, PileId.Waste);
    }
  }

  public async gameStateChanged(gameState: GameState): Promise<void> {
    this.cardViewModels.forEach((cvm) => {
      cvm.enableInput = gameState === GameState.InProgress;
    });

    if (gameState === GameState.AutoFinish && !this.autoFinishingGame) {
      this.autoFinishingGame = true;

      const isTrue = true;
      while (isTrue) {
        // Wait for all card to be disclosed before attempting to auto finish the deck,
        // we might still be waiting for the dealer to return from await discloseCards()
        if (this.gameServices.gameRunnerService.allCardsDisclosed) {
          break;
        }
        await delay(50);
      }

      while (isTrue) {
        if (!this.gameServices.gameRunnerService.autoFinishNextCard()) {
          break;
        }
        await delay(100 * this.instantFinishScaler);
      }

      while (isTrue) {
        if (this.cardViewModels.every((vm) => vm.isMoveCompleted)) {
          break;
        }
        await delay(50);
      }

      this.gameServices.gameRunnerService.gameComplete();
    }
  }

  public handleHintShake(possibleMoves: PossibleMove[]): void {
    if (possibleMoves.length === 0) {
      return;
    }

    if (this.canShakeEmptyDeck(possibleMoves)) {
      this.shakeEmptyDeck();
      return;
    }

    const topDeckCardToShake = this.canShakeTopDeckCard(possibleMoves);
    if (topDeckCardToShake !== undefined) {
      const topCardViewModel = this.allCardViewModels.find((cardViewModel) =>
        cardViewModel.matchesCardIndex(topDeckCardToShake.index),
      );
      if (topCardViewModel !== undefined) {
        this.shakeCardStack(topCardViewModel);
        return;
      }
    }

    this.allCardViewModels.forEach((cardViewModel) => {
      if (cardViewModel.canShakeForHints(possibleMoves)) {
        this.shakeCardStack(cardViewModel);
      }
    });
  }

  private shakeEmptyDeck(): void {
    this.deckPileViewModel.shakePile();
  }

  private shakeCardStack(cardViewModel: CardViewModel) {
    this.cardViewModels.forEach((cvm) => {
      if (cvm.isHigherInTableauStack(cardViewModel)) {
        cvm.shakeCard();
      }
    });
    cardViewModel.shakeCard();
  }

  private canShakeEmptyDeck(possibleMoves: PossibleMove[]): boolean {
    const game = this.gameServices.gameRunnerService.currentGame;
    if (game === undefined) {
      return false;
    }

    return (
      possibleMoves.every((move) => move.originPileId === PileId.Waste) &&
      !possibleMoves.some((move) => move.cardIndex === game.board.waste.lastCard?.index) &&
      !game.board.deck.hasCards
    );
  }

  private canShakeTopDeckCard(possibleMoves: PossibleMove[]): Card | undefined {
    const game = this.gameServices.gameRunnerService.currentGame;
    if (game === undefined) {
      return undefined;
    }

    const allCardsInDeck =
      possibleMoves.every((move) => move.originPileId === PileId.Deck) && game.board.deck.hasCards;

    const allCardsInWasteButNotTopCard =
      possibleMoves.every((move) => move.originPileId === PileId.Waste) &&
      !possibleMoves.some((move) => move.cardIndex === game.board.waste.lastCard?.index) &&
      game.board.deck.hasCards;

    if (allCardsInDeck || allCardsInWasteButNotTopCard) {
      return game.board.deck.lastCard;
    }

    return undefined;
  }
}
